import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { financeService } from '../services/api';
import { useLanguage } from '../context/LanguageContext';

const FinanceInvoice = () => {
    const { code } = useParams();
    const navigate = useNavigate();
    const { language } = useLanguage();
    const isRtl = language === 'fa';
    const t = (fa, en) => (isRtl ? fa : en);
    const printRef = useRef(null);

    const [invoiceData, setInvoiceData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchInvoice();
    }, [code]);

    const fetchInvoice = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await financeService.getInvoice(code);
            setInvoiceData(data);
        } catch (err) {
            console.error('Invoice fetch error:', err);
            setError(t('خطا در دریافت فاکتور', 'Error loading invoice'));
        } finally {
            setLoading(false);
        }
    };

    // PDF olarak kaydet (tarayıcı print dialog'u)
    const handleDownloadPDF = () => {
        const originalTitle = document.title;
        document.title = `${t('فاکتور', 'Invoice')}-${code}`;
        window.print();
        setTimeout(() => {
            document.title = originalTitle;
        }, 100);
    };

    // Direkt HTML olarak indir - CSS ile birlikte
    const handleDownloadHTML = () => {
        const content = printRef.current;
        if (!content) return;
        
        const styles = `
            @page { size: A4 landscape; margin: 5mm 10mm; }
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { 
                font-family: 'Tahoma', 'IRANSans', sans-serif; 
                background-color: #fff; 
                padding: 20px; 
                direction: rtl; 
            }
            .invoice-container {
                max-width: 920px;
                margin: 0 auto;
                background: #fff;
                padding: 12px 14px;
                border: 1px solid #ccc;
            }
            @media print {
                body { padding: 0; }
                .invoice-container { border: none; padding: 5mm; max-width: 100%; }
            }
            h2 { text-align: center; font-size: 15px; margin: 0 0 2px 0; font-weight: 900; letter-spacing: 0.5px; }
            .sub-text { text-align: center; font-size: 10px; margin: 0 0 8px 0; color: #333; font-weight: 500; }
            .top-meta { display: flex; justify-content: space-between; align-items: center; font-size: 12px; margin-bottom: 4px; font-weight: 600; gap: 20px; }
            table { 
                width: 100%; 
                border-collapse: collapse; 
                font-size: 9px; 
                border: 1.5px solid #000; 
                min-width: 880px;
            }
            th, td { 
                border: 1px solid #000; 
                padding: 3px 2px; 
                text-align: center; 
                vertical-align: middle; 
                white-space: nowrap;
            }
            th { 
                font-weight: 800; 
                background-color: #f4f4f4; 
                font-size: 8.5px; 
            }
            .td-desc { text-align: right; padding-right: 8px; white-space: normal; min-width: 130px; }
            .text-center { text-align: center; }
            .text-right { text-align: right; }
            .font-bold { font-weight: 700; }
            .font-extrabold { font-weight: 800; }
            .font-black { font-weight: 900; }
            .font-semibold { font-weight: 600; }
            .bg-gray-100 { background-color: #f4f4f4; }
            .bg-gray-200 { background-color: #e8e8e8; }
            .bg-gray-50 { background-color: #fdfdfd; }
            .whitespace-nowrap { white-space: nowrap; }
            .whitespace-normal { white-space: normal; }
            .min-w-\[90px\] { min-width: 90px; }
            .min-w-\[130px\] { min-width: 130px; }
            .min-w-\[200px\] { min-width: 200px; }
            .min-w-\[280px\] { min-width: 280px; }
            .flex { display: flex; }
            .flex-wrap { flex-wrap: wrap; }
            .items-start { align-items: flex-start; }
            .items-center { align-items: center; }
            .justify-between { justify-content: space-between; }
            .justify-around { justify-content: space-around; }
            .gap-2 { gap: 6px; }
            .gap-4 { gap: 14px; }
            .gap-5 { gap: 20px; }
            .gap-10 { gap: 40px; }
            .flex-1 { flex: 1; }
            .dbox {
                width: 15px;
                height: 15px;
                border: 1.5px solid #000;
                display: inline-block;
                background: #fff;
                text-align: center;
                font-size: 10px;
                line-height: 15px;
                font-weight: 700;
                flex-shrink: 0;
            }
            .digit-boxes { display: inline-flex; gap: 2px; align-items: center; direction: ltr; }
            .info-card { border: 1.5px solid #000; margin-bottom: 8px; border-radius: 2px; overflow: hidden; }
            .info-card-title { background-color: #e8e8e8; font-weight: 900; font-size: 12px; text-align: center; padding: 5px 8px; border-bottom: 1.5px solid #000; letter-spacing: 0.5px; }
            .info-card-body { padding: 6px 8px; font-size: 10px; }
            .info-row { display: flex; flex-wrap: wrap; align-items: flex-start; gap: 6px 14px; padding: 3px 0; border-bottom: 1px dotted #ccc; }
            .info-row:last-child { border-bottom: none; }
            .info-label { font-weight: 700; white-space: nowrap; font-size: 10px; }
            .info-field { display: inline-flex; align-items: center; gap: 3px; flex-wrap: wrap; }
            .border-black { border: 1.5px solid #000 !important; }
            .border-b { border-bottom: 1px dotted #ccc; }
            .border-b-black { border-bottom: 1.5px solid #000 !important; }
            .rounded-sm { border-radius: 2px; }
            .overflow-hidden { overflow: hidden; }
            .overflow-x-auto { overflow-x: auto; }
            .p-1 { padding: 3px 2px; }
            .p-2 { padding: 6px 8px; }
            .p-3 { padding: 8px 12px; }
            .py-1 { padding-top: 3px; padding-bottom: 3px; }
            .py-1\.5 { padding-top: 5px; padding-bottom: 5px; }
            .px-2 { padding-left: 8px; padding-right: 8px; }
            .pr-2 { padding-right: 8px; }
            .mb-0 { margin-bottom: 0; }
            .mb-1 { margin-bottom: 4px; }
            .mb-2 { margin-bottom: 8px; }
            .mt-1 { margin-top: 4px; }
            .mt-1\.5 { margin-top: 6px; }
            .mt-2 { margin-top: 8px; }
            .mt-5 { margin-top: 20px; }
            .text-xs { font-size: 10px; }
            .text-\[9px\] { font-size: 9px; }
            .text-\[10px\] { font-size: 10px; }
            .text-\[11px\] { font-size: 11px; }
            .text-\[15px\] { font-size: 15px; }
            .text-\[8\.5px\] { font-size: 8.5px; }
            .leading-relaxed { line-height: 1.8; }
            .tracking-wide { letter-spacing: 0.5px; }
            .payment-info { border: 1.5px solid #000; padding: 8px 12px; margin-top: 8px; font-size: 10px; font-weight: 600; background-color: #fdfdfd; line-height: 1.8; text-align: center; }
            .amount-words { display: flex; align-items: center; gap: 6px; margin-top: 8px; font-size: 11px; font-weight: 600; padding: 6px 0; }
            .signatures { display: flex; justify-content: space-around; margin-top: 20px; font-size: 11px; gap: 40px; }
            .signatures div { padding-top: 6px; text-align: center; min-width: 120px; font-weight: 600; }
            .table-wrapper { overflow-x: auto; margin-top: 6px; }
            tfoot td { font-weight: 800; background-color: #f9f9f9; font-size: 9px; }
            .no-line { border-bottom: none !important; min-width: auto !important; }
            @media print {
                body { background: #fff; padding: 0; }
                .invoice-container { box-shadow: none; border: none; max-width: 100%; padding: 5mm; }
                .info-card { border: 1px solid #000; }
                table { font-size: 8px; min-width: auto; }
            }
        `;
        
        const htmlContent = `<!DOCTYPE html>
<html lang="fa" dir="rtl">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${t('فاکتور', 'Invoice')}-${code}</title>
    <style>${styles}</style>
</head>
<body>
    <div class="invoice-container">
        ${content.innerHTML}
    </div>
</body>
</html>`;
        
        const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${t('فاکتور', 'Invoice')}-${code}.html`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const renderDigitBoxes = (value) => {
        const str = String(value || '').replace(/\D/g, '');
        return (
            <span className="inline-flex flex-row-reverse gap-[2px] print:gap-[1px]">
                {str.split('').map((digit, i) => (
                    <span key={i} className="w-[15px] h-[15px] border border-black inline-flex items-center justify-center text-[10px] font-bold bg-white flex-shrink-0 print:w-[13px] print:h-[13px] print:text-[8px]">
                        {digit}
                    </span>
                ))}
            </span>
        );
    };

    const getInvoiceTitle = () => {
        if (!invoiceData?.payment) return '';
        const { payment } = invoiceData;
        const eventTitle = invoiceData.event?.title || '';
        
        let prefix = '';
        if (!(payment.paid && payment.status === 3)) {
            prefix = t('پیش‌فاکتور', 'Proforma');
        } else {
            prefix = t('فاکتور', 'Invoice');
        }
        
        let typeText = '';
        if (payment.type === 1) {
            typeText = t('پیش‌پرداخت', 'Advance Payment');
        }
        
        return [prefix, typeText, eventTitle].filter(Boolean).join(' ');
    };

    const formatDate = (iso) => {
        if (!iso) return '';
        try {
            return new Date(iso).toLocaleDateString(isRtl ? 'fa-IR' : 'en-US');
        } catch { return ''; }
    };

    const formatNumber = (num) => {
        if (num === null || num === undefined) return '0';
        return Number(num).toLocaleString(isRtl ? 'fa-IR' : 'en-US');
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-screen font-iransansRegular">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-10 h-10 border-4 border-[#292DD8] border-t-transparent rounded-full animate-spin" />
                    <p className="text-[#292DD8] font-bold text-sm">{t('در حال بارگذاری...', 'Loading...')}</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex justify-center items-center min-h-screen font-iransansRegular">
                <div className="flex flex-col items-center gap-4 bg-red-50 rounded-xl p-8">
                    <p className="text-red-600 font-bold text-sm">{error}</p>
                    <button onClick={() => navigate(-1)} className="px-5 py-2 bg-red-500 text-white rounded-lg text-sm font-bold hover:bg-red-600">
                        {t('بازگشت', 'Go Back')}
                    </button>
                </div>
            </div>
        );
    }

    if (!invoiceData) return null;

    const { seller, buyer, items, total, total_in_words, payment } = invoiceData;

    return (
        <div className="min-h-screen bg-gray-200 py-6 px-3 font-iransansRegular print:bg-white print:py-0 print:px-0" dir="rtl">
            {/* Download Buttons - print'te gizle */}
            <div className="max-w-[920px] mx-auto mb-3 flex justify-between items-center print:hidden">
                <button onClick={() => navigate(-1)} className="text-[#204286] font-bold text-xs hover:text-[#292DD8] transition-colors flex items-center gap-1">
                    <svg className="w-4 h-4 rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                    {t('بازگشت', 'Back')}
                </button>
                
                <div className="flex gap-2">
                    <button onClick={handleDownloadHTML} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-bold hover:bg-green-700 transition-colors shadow-md">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        {t('دانلود HTML', 'Download HTML')}
                    </button>
                    <button onClick={handleDownloadPDF} className="flex items-center gap-2 px-4 py-2 bg-[#292DD8] text-white rounded-lg text-sm font-bold hover:bg-[#1f23a8] transition-colors shadow-md">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                        </svg>
                        {t('ذخیره به عنوان PDF', 'Save as PDF')}
                    </button>
                </div>
            </div>

            {/* Sadece invoice kısmı - print'te tek sayfa */}
            <style>{`
                @media print {
                    @page { size: A4 landscape; margin: 5mm; }
                    body * { visibility: hidden; }
                    #invoice-print-area, #invoice-print-area * { visibility: visible; }
                    #invoice-print-area { 
                        position: absolute; 
                        left: 0; 
                        top: 0; 
                        width: 100%; 
                    }
                }
            `}</style>
            <div id="invoice-print-area" ref={printRef} className="max-w-[920px] mx-auto bg-white p-3 sm:p-4 shadow-lg border border-gray-300 print:shadow-none print:border-none print:max-w-full print:p-1">
                
                {/* Top Meta */}
                <div className="flex justify-between items-center text-xs font-semibold mb-1 gap-5">
                    <span>{t('شماره:', 'No.')} <span>{payment?.code || ''}</span></span>
                    <span>{t('تاریخ:', 'Date:')} <span>{formatDate(payment?.created_at)}</span></span>
                </div>

                {/* Title */}
                <h2 className="text-center text-[15px] font-black mb-0 tracking-wide print:text-[13px]">
                    {getInvoiceTitle()}
                </h2>
                <p className="text-center text-[10px] text-gray-600 mb-2 print:text-[8px]">
                    {t('(بدون احتساب ارزش افزوده)', '(Excluding VAT)')}
                </p>

                {/* Seller Info */}
                <div className="border border-black rounded-sm mb-2 overflow-hidden print:mb-1">
                    <div className="bg-gray-200 font-black text-xs text-center py-1.5 border-b border-black tracking-wide print:text-[10px] print:py-1">
                        {t('مشخصات فروشنده', 'Seller Information')}
                    </div>
                    <div className="p-2 text-[10px] print:text-[8px] print:p-1">
                        <div className="flex flex-wrap items-start gap-x-4 gap-y-1 py-0.5 border-b border-dotted border-gray-300">
                            <span className="font-bold whitespace-nowrap">{t('نام حقیقی/حقوقی:', 'Name:')}</span>
                            <span className="min-w-[280px]">{seller?.name || ''}</span>
                            <span className="font-bold whitespace-nowrap">{t('شماره اقتصادی:', 'Economic Code:')}</span>
                            {renderDigitBoxes(seller?.economic_code)}
                        </div>
                        <div className="flex flex-wrap items-start gap-x-4 gap-y-1 py-0.5 border-b border-dotted border-gray-300">
                            <span className="font-bold whitespace-nowrap">{t('شماره ثبت:', 'Reg. No:')}</span>
                            {renderDigitBoxes(seller?.registration_number)}
                            <span className="font-bold whitespace-nowrap">{t('شناسه ملی:', 'National ID:')}</span>
                            {renderDigitBoxes(seller?.national_id)}
                        </div>
                        <div className="flex flex-wrap items-start gap-x-4 gap-y-1 py-0.5 border-b border-dotted border-gray-300">
                            <span className="font-bold whitespace-nowrap">{t('استان:', 'Province:')}</span><span className="min-w-[90px]">{seller?.province || ''}</span>
                            <span className="font-bold whitespace-nowrap">{t('شهر:', 'City:')}</span><span className="min-w-[90px]">{seller?.city || ''}</span>
                            <span className="font-bold whitespace-nowrap">{t('نشانی:', 'Address:')}</span><span className="flex-1 min-w-[200px]">{seller?.address || ''}</span>
                        </div>
                        <div className="flex flex-wrap items-start gap-x-4 gap-y-1 py-0.5">
                            <span className="font-bold whitespace-nowrap">{t('کد پستی:', 'Postal Code:')}</span>
                            {renderDigitBoxes(seller?.postal_code)}
                            <span className="font-bold whitespace-nowrap">{t('شماره تلفن / نمابر:', 'Phone/Fax:')}</span>
                            <span className="min-w-[90px]">{seller?.phone || ''}</span>
                        </div>
                    </div>
                </div>

                {/* Buyer Info */}
                <div className="border border-black rounded-sm mb-2 overflow-hidden print:mb-1">
                    <div className="bg-gray-200 font-black text-xs text-center py-1.5 border-b border-black tracking-wide print:text-[10px] print:py-1">
                        {t('مشخصات خریدار', 'Buyer Information')}
                    </div>
                    <div className="p-2 text-[10px] print:text-[8px] print:p-1">
                        <div className="flex flex-wrap items-start gap-x-4 gap-y-1 py-0.5 border-b border-dotted border-gray-300">
                            <span className="font-bold whitespace-nowrap">{t('نام حقیقی/حقوقی:', 'Name:')}</span>
                            <span className="min-w-[280px]">{buyer?.name || ''}</span>
                            <span className="font-bold whitespace-nowrap">{t('شماره اقتصادی:', 'Economic Code:')}</span>
                            {renderDigitBoxes(buyer?.economic_code)}
                        </div>
                        <div className="flex flex-wrap items-start gap-x-4 gap-y-1 py-0.5 border-b border-dotted border-gray-300">
                            <span className="font-bold whitespace-nowrap">{t('شماره ثبت:', 'Reg. No:')}</span>
                            {renderDigitBoxes(buyer?.registration_number)}
                            <span className="font-bold whitespace-nowrap">{t('شناسه ملی:', 'National ID:')}</span>
                            {renderDigitBoxes(buyer?.national_id)}
                        </div>
                        <div className="flex flex-wrap items-start gap-x-4 gap-y-1 py-0.5 border-b border-dotted border-gray-300">
                            <span className="font-bold whitespace-nowrap">{t('استان:', 'Province:')}</span><span className="min-w-[90px]">{buyer?.province || ''}</span>
                            <span className="font-bold whitespace-nowrap">{t('شهر:', 'City:')}</span><span className="min-w-[90px]">{buyer?.city || ''}</span>
                            <span className="font-bold whitespace-nowrap">{t('نشانی:', 'Address:')}</span><span className="flex-1 min-w-[200px]">{buyer?.address || ''}</span>
                        </div>
                        <div className="flex flex-wrap items-start gap-x-4 gap-y-1 py-0.5">
                            <span className="font-bold whitespace-nowrap">{t('کد پستی:', 'Postal Code:')}</span>
                            {renderDigitBoxes(buyer?.postal_code)}
                            <span className="font-bold whitespace-nowrap">{t('شماره تلفن / نمابر:', 'Phone/Fax:')}</span>
                            <span className="min-w-[90px]">{buyer?.phone_number || ''}</span>
                        </div>
                    </div>
                </div>

                {/* Items Table */}
                <div className="overflow-x-auto mt-1.5">
                    <table className="w-full border-collapse text-[9px] border border-black min-w-[880px] print:text-[7px]">
                        <thead>
                            <tr>
                                <th rowSpan={2} className="border border-black p-1 text-center font-extrabold bg-gray-100 text-[8.5px] w-[4%] print:text-[7px]">{t('ردیف', '#')}</th>
                                <th rowSpan={2} className="border border-black p-1 text-center font-extrabold bg-gray-100 text-[8.5px] w-[7%] print:text-[7px]">{t('کد کالا', 'Code')}</th>
                                <th rowSpan={2} className="border border-black p-1 text-center font-extrabold bg-gray-100 text-[8.5px] w-[16%] print:text-[7px]">{t('شرح کالا / خدمات', 'Description')}</th>
                                <th rowSpan={2} className="border border-black p-1 text-center font-extrabold bg-gray-100 text-[8.5px] w-[6%] print:text-[7px]">{t('تعداد', 'Qty')}</th>
                                <th rowSpan={2} className="border border-black p-1 text-center font-extrabold bg-gray-100 text-[8.5px] w-[7%] print:text-[7px]">{t('واحد', 'Unit')}</th>
                                <th colSpan={4} className="border border-black p-1 text-center font-extrabold bg-gray-100 text-[8.5px] w-[60%] print:text-[7px]">{t('مبلغ (ریال)', 'Amount (IRR)')}</th>
                            </tr>
                            <tr>
                                <th className="border border-black p-1 text-center font-extrabold bg-gray-100 text-[8.5px] w-[9%] print:text-[7px]">{t('مبلغ واحد', 'Unit Price')}</th>
                                <th className="border border-black p-1 text-center font-extrabold bg-gray-100 text-[8.5px] w-[7%] print:text-[7px]">{t('تخفیف', 'Discount')}</th>
                                <th className="border border-black p-1 text-center font-extrabold bg-gray-100 text-[8.5px] w-[10%] print:text-[7px]">{t('مالیات', 'Tax')}</th>
                                <th className="border border-black p-1 text-center font-extrabold bg-gray-100 text-[8.5px] w-[12%] print:text-[7px]">{t('جمع کل', 'Total')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(items || []).map((item, index) => (
                                <tr key={index}>
                                    <td className="border border-black p-1 text-center">{index + 1}</td>
                                    <td className="border border-black p-1 text-center">{item.code}</td>
                                    <td className="border border-black p-1 text-right pr-2 whitespace-normal min-w-[130px]">{item.description}</td>
                                    <td className="border border-black p-1 text-center">{item.quantity}</td>
                                    <td className="border border-black p-1 text-center">{item.unit}</td>
                                    <td className="border border-black p-1 text-center">{formatNumber(item.unit_price)}</td>
                                    <td className="border border-black p-1 text-center">{formatNumber(item.discount)}</td>
                                    <td className="border border-black p-1 text-center">{formatNumber(item.tax)}</td>
                                    <td className="border border-black p-1 text-center font-bold">{formatNumber(item.total)}</td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td colSpan={5} className="border border-black p-1 text-center font-black">{t('جمع کل', 'Grand Total')}</td>
                                <td colSpan={4} className="border border-black p-1 text-center font-black">{formatNumber(total)}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>

                {/* Payment Info */}
                <div className="border border-black p-2 sm:p-3 mt-2 text-[10px] font-semibold bg-gray-50 text-center leading-relaxed print:text-[8px] print:p-1.5 print:mt-1">
                    <span className="font-black text-[11px] print:text-[9px]">{t('اطلاعات پرداخت:', 'Payment Info:')}</span>{' '}
                    {seller?.bank_name} &nbsp;|&nbsp; {t('شماره شبا:', 'IBAN:')} <strong>{seller?.IBAN}</strong><br />
                    {t('شماره حساب:', 'Account:')} <strong>{seller?.account_number}</strong> &nbsp;|&nbsp; {t('به نام:', 'Name:')} <strong>{seller?.account_holder}</strong>
                </div>

                {/* Amount in Words */}
                <div className="flex items-center gap-2 mt-2 text-[11px] font-semibold py-1.5 print:text-[9px] print:mt-1">
                    <span>{t('مبلغ به حروف:', 'Amount in words:')}</span>
                    <span className="flex-1 min-w-[200px]">{total_in_words || ''}</span>
                </div>

                {/* Signatures */}
                <div className="flex justify-around mt-5 text-[11px] gap-10 print:text-[9px] print:mt-3">
                    <div className="text-center min-w-[120px] font-semibold pt-1.5">
                        {t('امضاء فروشنده', 'Seller Signature')}
                    </div>
                    <div className="text-center min-w-[120px] font-semibold pt-1.5">
                        {t('امضاء خریدار', 'Buyer Signature')}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FinanceInvoice;